// i18n keys of a client account's type, group and description (batch 5 round 4, COA-6 / COA-11): the
// chart of accounts screen (src/components/Bookkeeping/ChartOfAccounts.tsx and AccountTypeBadge.tsx),
// en / zh / th. ⛔ Fill all three: an empty value falls back or renders blank. ⛔ No `_plural` (dead in
// i18next 23): the one count here is written "{{n}} not classified", which reads right for any n.
// ⛔ ONLY THE SCREEN'S OWN WORDS. The sixteen Account Type words and every group / Subgroup word are the
// customer's own and are shown exactly as written in every language; so are codes, names and Dr / Cr.
export const en: Record<string, string> = {
  coat_edit: 'Edit',
  coat_edit_tip: 'Edit the name, type, group, description and sections',
  coat_col_code: 'Code',
  coat_col_account: 'Account',
  coat_col_group_type: 'Group / Type',
  coat_col_normal: 'Normal',
  coat_col_balance: 'Balance',
  coat_normal_tip: 'The side this account normally keeps: its class\'s side, turned over for a contra type',
  coat_type: 'Type',
  coat_type_none: 'Not classified',
  coat_group: 'Group',
  coat_group_ph: 'Group: a Subgroup word, or your own',
  coat_description: 'Description',
  coat_description_ph: 'Description (for people; not printed in the statutory books)',
  coat_class: 'Class',
  coat_unclassified: '{{n}} not classified',
  coat_unclassified_tip: 'Show only the accounts nobody has given a type yet. They post as every account always has.',
  coat_show_all: 'Show all accounts',
  coat_search_ph: 'Search code, name, description, group or type',
  coat_no_match: 'No account matches.',
  coat_words_note: 'Type and group words are the customer\'s own and are shown as written, in every language.',
  coat_kind_header: 'Groups sub-accounts; nothing is posted to it',
  coat_kind_control: 'Control account: carries the AR or AP subsidiary ledger',
  coat_kind_contra: 'Offsets the account it is paired with, so it normally keeps the other side',
  coat_kind_system: 'Used by the system; nothing is posted to it by hand',
  coat_kind_reference: 'Reference data; nothing is posted to it',
  coat_kind_posting: 'Entries are posted to it',
  coat_control_badge: 'Control {{role}}',
  coat_unavailable: '{{word}} (not available: {{why}})',
  coat_why_unknown_type: 'not one of the type words',
  coat_why_control_frozen: 'this account controls AR or AP, and a control account\'s type never changes',
  coat_why_posted_to_nonposting: 'nothing is posted to this type, and this account already carries lines',
  coat_why_role_on_nonposting: 'nothing is posted to this type, and the system posts to this account for its role (give the role to another account first)',
  coat_why_role_on_contra: 'a contra account carries the opposite side of another account\'s balance, and the system posts its own figures to this account for its role (give the role to another account first)',
  coat_why_header_needs_children: 'a header groups sub-accounts, and this account has none',
  coat_why_posted_control: 'this account carries lines, so its control cannot change',
  coat_why_control_needs_role: 'a control account must control AR or AP',
  coat_why_control_on_nonposting: 'this account controls AR or AP, whose entries are posted to it',
  coat_why_control_on_contra: 'this account controls AR or AP, and a control account cannot be a Contra type',
  coat_why_type_class: 'it stands only on {{classes}} accounts',
  // b13 S2 (COA-6's parent half): the Parent field, the tree, and why a choice is not offered. coat_why_<rule> for every
  // tree rule (services/coaRules.ts CHART_TREE_RULES) and removeAccount's has-children.
  coat_parent: 'Parent account',
  coat_parent_top: '(top level)',
  coat_parent_why: 'The account this one sits under. Only accounts of the same class that may hold sub-accounts are offered — a Header, or an account entries are posted to that controls no AR / AP ledger — and never one of its own sub-accounts.',
  coat_class_tree_locked: 'The class cannot change while this account sits under a parent or holds sub-accounts: move it out of its parent, or move its sub-accounts, first.',
  coat_tree_problem: 'Its stored parent breaks a chart rule ({{why}}). It was written outside this screen and is shown at the top level until it is fixed here.',
  coat_why_parent_not_found: 'the parent is not an account of this chart',
  coat_why_parent_self: 'an account cannot be its own parent',
  coat_why_parent_cycle: 'it would sit under one of its own sub-accounts',
  coat_why_parent_class: 'a sub-account must be of its parent\'s class',
  coat_why_parent_kind: 'only a Header, or an account entries are posted to that controls no AR / AP ledger, can hold sub-accounts',
  coat_why_header_needs_postable: 'a header must have, somewhere under it, an account entries are posted to',
  coat_why_has_children: 'it has sub-accounts — give them another parent first',
  coat_why_role_held: 'an administrator gave it a role — take the role off it first',
  coat_why_role_held_template: 'it holds {{role}} from the template — removed, the role would have no account; an administrator gives the role to another account first',
  coat_parent_locked_header_needs_children: 'it is the last sub-account of its Header, which must keep one. Give the Header another sub-account first (or change the Header\'s type), then move this one.',
  coat_parent_locked_header_needs_postable: 'it is the last account entries are posted to under its Header. Put another such account under the Header first, then move this one.',
  // (b13 review) A stored parent the Parent select does not offer (it breaks a rule as stored) — named in words, never by
  // its id: by code and name when it is an account of this chart, else as not being one.
  coat_parent_stored: '{{code}} · {{name}} (as stored — not offered)',
  coat_parent_missing: '(the stored parent is not an account of this chart)',
};

export const zh: Record<string, string> = {
  coat_edit: '编辑',
  coat_edit_tip: '编辑名称、类型、分组、描述和归类',
  coat_col_code: '代码',
  coat_col_account: '科目',
  coat_col_group_type: '分组 / 类型',
  coat_col_normal: '正常余额',
  coat_col_balance: '余额',
  coat_normal_tip: '该科目通常的余额方向:取其类别的方向,抵减类型则相反',
  coat_type: '类型',
  coat_type_none: '未分类',
  coat_group: '分组',
  coat_group_ph: '分组:Subgroup 原词,或自行输入',
  coat_description: '描述',
  coat_description_ph: '描述(供人阅读,不打印在法定账簿上)',
  coat_class: '类别',
  coat_unclassified: '{{n}} 个未分类',
  coat_unclassified_tip: '只显示尚未设定类型的科目。它们照常可以记账。',
  coat_show_all: '显示全部科目',
  coat_search_ph: '按代码、名称、描述、分组或类型搜索',
  coat_no_match: '没有符合条件的科目。',
  coat_words_note: '类型和分组用的是客户的原词,在所有语言中都按原样显示。',
  coat_kind_header: '归集下级科目,不直接记账',
  coat_kind_control: '控制科目:承载应收或应付明细账',
  coat_kind_contra: '抵减与之配对的科目,因此通常记相反方向',
  coat_kind_system: '由系统使用,不手工记账',
  coat_kind_reference: '参考数据,不记账',
  coat_kind_posting: '可以记账',
  coat_control_badge: '控制 {{role}}',
  coat_unavailable: '{{word}}(不可用:{{why}})',
  coat_why_unknown_type: '不是类型词表中的词',
  coat_why_control_frozen: '本科目是应收(AR)或应付(AP)的控制科目,控制科目的类型不能更改',
  coat_why_posted_to_nonposting: '该类型不能记账,而本科目已有分录行',
  coat_why_role_on_nonposting: '该类型不能记账,而系统按本科目的用途向它记账(请先把该用途交给另一个科目)',
  coat_why_role_on_contra: '备抵科目记的是另一个科目余额的反方向,而系统按本科目的用途向它记自己的金额(请先把该用途交给另一个科目)',
  coat_why_header_needs_children: '表头科目用来归集下级科目,而本科目没有下级科目',
  coat_why_posted_control: '本科目已有分录行,控制属性不能更改',
  coat_why_control_needs_role: '控制科目必须控制应收(AR)或应付(AP)',
  coat_why_control_on_nonposting: '本科目控制应收(AR)或应付(AP),其分录要记到本科目',
  coat_why_control_on_contra: '本科目是应收(AR)或应付(AP)的控制科目,控制科目不能是抵减(Contra)类型',
  coat_why_type_class: '只能用于{{classes}}类科目',
  coat_parent: '上级科目',
  coat_parent_top: '(顶层)',
  coat_parent_why: '本科目所归属的上级科目。只列出同一类别、可以下设子科目的科目——表头科目,或可记账且不控制应收(AR)/应付(AP)明细账的科目——且不会列出本科目自己的下级科目。',
  coat_class_tree_locked: '本科目有上级科目或下设子科目时,类别不能更改:请先把它移出上级科目,或先移走它的子科目。',
  coat_tree_problem: '其已存的上级科目违反科目表规则({{why}})。这是在本页面之外写入的;修正之前按顶层科目显示。',
  coat_why_parent_not_found: '上级科目不是本科目表中的科目',
  coat_why_parent_self: '科目不能作为自己的上级科目',
  coat_why_parent_cycle: '会挂到自己的下级科目之下',
  coat_why_parent_class: '子科目必须与上级科目同类',
  coat_why_parent_kind: '只有表头科目,或可记账且不控制应收(AR)/应付(AP)明细账的科目,才能下设子科目',
  coat_why_header_needs_postable: '表头科目之下必须有可记账的科目',
  coat_why_has_children: '本科目下设子科目——请先为子科目另选上级科目',
  coat_why_role_held: '管理员给了它一个用途——请先取下这个用途',
  coat_why_role_held_template: '它从模板持有{{role}}——删除后该用途就没有科目了;请管理员先把该用途交给另一个科目',
  coat_parent_locked_header_needs_children: '它是其表头科目下最后一个子科目,表头科目至少要保留一个。请先给表头科目另加一个子科目(或更改表头科目的类型),再移动本科目。',
  coat_parent_locked_header_needs_postable: '它是其表头科目下最后一个可记账的科目。请先在该表头科目下另加一个可记账科目,再移动本科目。',
  coat_parent_stored: '{{code}} · {{name}}(已存值——不可选)',
  coat_parent_missing: '(已存的上级科目不是本科目表中的科目)',
};

export const th: Record<string, string> = {
  coat_edit: 'แก้ไข',
  coat_edit_tip: 'แก้ไขชื่อ ประเภท กลุ่ม คำอธิบาย และหมวดในงบ',
  coat_col_code: 'รหัส',
  coat_col_account: 'บัญชี',
  coat_col_group_type: 'กลุ่ม / ประเภท',
  coat_col_normal: 'ยอดปกติ',
  coat_col_balance: 'ยอดคงเหลือ',
  coat_normal_tip: 'ด้านที่บัญชีนี้มียอดคงเหลือตามปกติ: ด้านของหมวดบัญชี และกลับด้านสำหรับประเภทบัญชีปรับลด',
  coat_type: 'ประเภท',
  coat_type_none: 'ยังไม่จัดประเภท',
  coat_group: 'กลุ่ม',
  coat_group_ph: 'กลุ่ม: คำจาก Subgroup หรือคำของคุณเอง',
  coat_description: 'คำอธิบาย',
  coat_description_ph: 'คำอธิบาย (สำหรับผู้ใช้ ไม่พิมพ์ในสมุดบัญชีตามกฎหมาย)',
  coat_class: 'หมวดบัญชี',
  coat_unclassified: 'ยังไม่จัดประเภท {{n}} บัญชี',
  coat_unclassified_tip: 'แสดงเฉพาะบัญชีที่ยังไม่มีใครกำหนดประเภท บัญชีเหล่านี้ยังบันทึกรายการได้ตามปกติ',
  coat_show_all: 'แสดงบัญชีทั้งหมด',
  coat_search_ph: 'ค้นหาตามรหัส ชื่อ คำอธิบาย กลุ่ม หรือประเภท',
  coat_no_match: 'ไม่มีบัญชีที่ตรงกับการค้นหา',
  coat_words_note: 'คำที่ใช้เป็นประเภทและกลุ่มเป็นคำของลูกค้าเอง แสดงตามที่เขียนไว้ในทุกภาษา',
  coat_kind_header: 'บัญชีสำหรับรวมบัญชีย่อย ไม่ใช้บันทึกรายการ',
  coat_kind_control: 'บัญชีคุม: รวมยอดบัญชีย่อยลูกหนี้หรือเจ้าหนี้',
  coat_kind_contra: 'บัญชีปรับลดของบัญชีที่จับคู่ จึงมียอดปกติอยู่ด้านตรงข้าม',
  coat_kind_system: 'ระบบเป็นผู้ใช้ ไม่บันทึกรายการด้วยมือ',
  coat_kind_reference: 'ข้อมูลอ้างอิง ไม่ใช้บันทึกรายการ',
  coat_kind_posting: 'ใช้บันทึกรายการได้',
  coat_control_badge: 'บัญชีคุม {{role}}',
  coat_unavailable: '{{word}} (ใช้ไม่ได้: {{why}})',
  coat_why_unknown_type: 'ไม่ใช่คำในรายการประเภทบัญชี',
  coat_why_control_frozen: 'บัญชีนี้เป็นบัญชีคุมลูกหนี้ (AR) หรือเจ้าหนี้ (AP) และประเภทของบัญชีคุมเปลี่ยนไม่ได้',
  coat_why_posted_to_nonposting: 'ประเภทนี้ไม่ใช้บันทึกรายการ และบัญชีนี้มีรายการบันทึกอยู่แล้ว',
  coat_why_role_on_nonposting: 'ประเภทนี้ไม่ใช้บันทึกรายการ และระบบบันทึกรายการเข้าบัญชีนี้ตามหน้าที่ของบัญชี (โปรดโอนหน้าที่นี้ให้บัญชีอื่นก่อน)',
  coat_why_role_on_contra: 'บัญชีปรับมูลค่าแสดงยอดด้านตรงข้ามของบัญชีอื่น และระบบบันทึกจำนวนเงินเข้าบัญชีนี้ตามหน้าที่ของบัญชี (โปรดโอนหน้าที่นี้ให้บัญชีอื่นก่อน)',
  coat_why_header_needs_children: 'บัญชีหัวข้อใช้รวมบัญชีย่อย แต่บัญชีนี้ไม่มีบัญชีย่อย',
  coat_why_posted_control: 'บัญชีนี้มีรายการบันทึกแล้ว จึงเปลี่ยนการเป็นบัญชีคุมไม่ได้',
  coat_why_control_needs_role: 'บัญชีคุมต้องคุมลูกหนี้ (AR) หรือเจ้าหนี้ (AP)',
  coat_why_control_on_nonposting: 'บัญชีนี้คุมลูกหนี้ (AR) หรือเจ้าหนี้ (AP) ซึ่งต้องบันทึกรายการเข้าบัญชีนี้',
  coat_why_control_on_contra: 'บัญชีนี้เป็นบัญชีคุมลูกหนี้ (AR) หรือเจ้าหนี้ (AP) และบัญชีคุมจะเป็นประเภทบัญชีปรับลด (Contra) ไม่ได้',
  coat_why_type_class: 'ใช้ได้เฉพาะกับบัญชีหมวด{{classes}}',
  coat_parent: 'บัญชีแม่',
  coat_parent_top: '(ระดับบนสุด)',
  coat_parent_why: 'บัญชีที่บัญชีนี้อยู่ภายใต้ จะแสดงเฉพาะบัญชีหมวดเดียวกันที่มีบัญชีย่อยได้ — บัญชีหัวข้อ หรือบัญชีที่บันทึกรายการได้และไม่ได้คุมบัญชีย่อยลูกหนี้ (AR) / เจ้าหนี้ (AP) — และไม่แสดงบัญชีย่อยของบัญชีนี้เอง',
  coat_class_tree_locked: 'เปลี่ยนหมวดบัญชีไม่ได้ขณะที่บัญชีนี้อยู่ภายใต้บัญชีแม่หรือมีบัญชีย่อย ให้ย้ายออกจากบัญชีแม่ หรือย้ายบัญชีย่อยออกก่อน',
  coat_tree_problem: 'บัญชีแม่ที่บันทึกไว้ขัดกับกฎของผังบัญชี ({{why}}) ซึ่งถูกบันทึกจากนอกหน้านี้ จึงแสดงไว้ที่ระดับบนสุดจนกว่าจะแก้ไขที่นี่',
  coat_why_parent_not_found: 'บัญชีแม่ไม่ใช่บัญชีในผังบัญชีนี้',
  coat_why_parent_self: 'บัญชีเป็นบัญชีแม่ของตัวเองไม่ได้',
  coat_why_parent_cycle: 'จะทำให้อยู่ภายใต้บัญชีย่อยของตัวเอง',
  coat_why_parent_class: 'บัญชีย่อยต้องอยู่หมวดเดียวกับบัญชีแม่',
  coat_why_parent_kind: 'เฉพาะบัญชีหัวข้อ หรือบัญชีที่บันทึกรายการได้และไม่ได้คุมบัญชีย่อยลูกหนี้ (AR) / เจ้าหนี้ (AP) เท่านั้นที่มีบัญชีย่อยได้',
  coat_why_header_needs_postable: 'ภายใต้บัญชีหัวข้อต้องมีบัญชีที่บันทึกรายการได้',
  coat_why_has_children: 'บัญชีนี้มีบัญชีย่อย ให้ย้ายบัญชีย่อยไปอยู่ใต้บัญชีแม่อื่นก่อน',
  coat_why_role_held: 'ผู้ดูแลระบบได้มอบหน้าที่ให้บัญชีนี้ — โปรดนำหน้าที่ออกก่อน',
  coat_why_role_held_template: 'บัญชีนี้มีหน้าที่{{role}}จากแม่แบบ — หากลบ หน้าที่นี้จะไม่มีบัญชีรองรับ ผู้ดูแลระบบต้องมอบหน้าที่ให้บัญชีอื่นก่อน',
  coat_parent_locked_header_needs_children: 'บัญชีนี้เป็นบัญชีย่อยบัญชีสุดท้ายของบัญชีหัวข้อ ซึ่งต้องมีบัญชีย่อยอย่างน้อยหนึ่งบัญชี ให้เพิ่มบัญชีย่อยอื่นใต้บัญชีหัวข้อก่อน (หรือเปลี่ยนประเภทของบัญชีหัวข้อ) แล้วจึงย้ายบัญชีนี้',
  coat_parent_locked_header_needs_postable: 'บัญชีนี้เป็นบัญชีสุดท้ายที่บันทึกรายการได้ภายใต้บัญชีหัวข้อ ให้เพิ่มบัญชีที่บันทึกรายการได้อีกบัญชีใต้บัญชีหัวข้อก่อน แล้วจึงย้ายบัญชีนี้',
  coat_parent_stored: '{{code}} · {{name}} (ตามที่บันทึกไว้ — เลือกไม่ได้)',
  coat_parent_missing: '(บัญชีแม่ที่บันทึกไว้ไม่ใช่บัญชีในผังบัญชีนี้)',
};
